export const PIEVE_TRIGGER_POLL_INTERVAL_MINUTES_MIN = 5;

export const DEFAULT_PIEVE_TRIGGER_POLL_INTERVAL_OPTIONS = Object.freeze([
  { value: 5, label: 'Every 5 minutes' },
  { value: 10, label: 'Every 10 minutes' },
  { value: 15, label: 'Every 15 minutes' },
  { value: 30, label: 'Every 30 minutes' },
  { value: 60, label: 'Every hour' },
  { value: 60 * 3, label: 'Every 3 hours' },
  { value: 60 * 6, label: 'Every 6 hours' },
  { value: 60 * 12, label: 'Every 12 hours' },
  { value: 60 * 24, label: 'Daily' },
  { value: 60 * 24 * 2, label: 'Every 2 days' },
  { value: 60 * 24 * 3, label: 'Every 3 days' },
  { value: 60 * 24 * 7, label: 'Weekly' },
  { value: 60 * 24 * 14, label: 'Every 2 weeks' },
  { value: 60 * 24 * 30, label: 'Monthly' }
]);

export const normalizePieveTriggerPollIntervalMinutes = (value, { min = PIEVE_TRIGGER_POLL_INTERVAL_MINUTES_MIN } = {}) => {
  const parsedValue = Number(value);

  if (!Number.isInteger(parsedValue) || parsedValue < min) {
    return null;
  }

  return parsedValue;
};

const buildIntervalUnitLabel = (intervalMinutes) => {
  const normalizedInterval = normalizePieveTriggerPollIntervalMinutes(intervalMinutes, { min: 1 });

  if (!normalizedInterval) {
    return '';
  }

  const minutesPerDay = 60 * 24;
  const minutesPerWeek = minutesPerDay * 7;

  if (normalizedInterval % minutesPerWeek === 0) {
    const weeks = normalizedInterval / minutesPerWeek;
    return weeks === 1 ? 'week' : `${weeks} weeks`;
  }

  if (normalizedInterval % minutesPerDay === 0) {
    const days = normalizedInterval / minutesPerDay;
    return days === 1 ? 'day' : `${days} days`;
  }

  if (normalizedInterval % 60 === 0) {
    const hours = normalizedInterval / 60;
    return hours === 1 ? 'hour' : `${hours} hours`;
  }

  return normalizedInterval === 1 ? 'minute' : `${normalizedInterval} minutes`;
};

const buildCustomTriggerPollIntervalOption = (intervalMinutes) => {
  const normalizedInterval = normalizePieveTriggerPollIntervalMinutes(intervalMinutes, { min: 1 });
  const intervalLabel = buildIntervalUnitLabel(normalizedInterval);

  if (!normalizedInterval || !intervalLabel) {
    return null;
  }

  return {
    value: normalizedInterval,
    label: `Every ${intervalLabel}`
  };
};

export const getPieveTriggerPollIntervalOptions = ({ selectedIntervalMinutes, options = DEFAULT_PIEVE_TRIGGER_POLL_INTERVAL_OPTIONS } = {}) => {
  const normalizedOptions = Array.isArray(options) ? options : DEFAULT_PIEVE_TRIGGER_POLL_INTERVAL_OPTIONS;
  const normalizedInterval = normalizePieveTriggerPollIntervalMinutes(selectedIntervalMinutes, { min: 1 });

  if (!normalizedInterval) {
    return normalizedOptions;
  }

  const existingOption = normalizedOptions.find((option) => option?.value === normalizedInterval);

  if (existingOption) {
    return normalizedOptions;
  }

  const customOption = buildCustomTriggerPollIntervalOption(normalizedInterval);

  if (!customOption) {
    return normalizedOptions;
  }

  const nextOptions = [...normalizedOptions];
  const insertionIndex = nextOptions.findIndex((option) => Number(option?.value) > normalizedInterval);

  if (insertionIndex < 0) {
    nextOptions.push(customOption);
    return nextOptions;
  }

  nextOptions.splice(insertionIndex, 0, customOption);
  return nextOptions;
};
